import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { finalize, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ON_HOLD_DEFAULT_COLUMNS,
  ON_HOLD_FILTER_MAPPING,
  ON_HOLD_FILTER_OPTIONS,
  ON_HOLD_TABLE_FILTER_TABS,
  ON_HOLD_VIEW_DETAILS_ACTION,
} from './on-hold-constant';
import { COLUMN_IDENTIFIERS } from '../../const';
import { isFeatureFlaggedColumnVisible } from '../../../../common/helpers/feature-flagged-table-columns/feature-flagged-table-columns.helper';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { RelatedRequestsDropdownComponent } from '../../../../../ui-components/lib/related-requests-dropdown/related-requests-dropdown.component';
import { SearchService } from '../../../../../ui-components/lib/search-field/search.service';
import { TruncatePipe } from '../../../../../ui-components/pipes/truncate/truncate.pipe';
import { TrimTooltipDirective } from '../../../../../ui-components/directives/trim-tooltip/trim-tooltip.directive';
import { UserDropdownViewComponent } from '../../../../../ui-components/lib/user-dropdown-view/user-dropdown-view.component';
import { FilterOption } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.model';
import { ActionListComponent } from '../../../../common/components/action-list/action-list.component';
import { RequestCreatorComponent } from '../../../../common/components/request-creator/request-creator.component';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { TenantPathPipe } from '../../../../common/pipes/tenant-path/tenant-path.pipe';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { FilterTableColByFeatFlagPipe } from '../../../../common/pipes/filter-table-col-by-featFlag/filter-table-col-by-feat-flag.pipe';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../ui-components/lib/table/table.model';
import { StepStatusEnum } from '../../../../common/enums/step-status';
import { ActionList } from '../../../../common/models/action-list.model';
import { DEFAULT_PAGE_SIZE } from '../../../../common/constants/pagination.constants';
import { EMPLOYEE_ROUTES } from '../../../../common/constants/employee.routing.constants';
import { MyOrganizationService } from '../../../../common/services/my-organization/my-organization.service';
import { RequestTypeEnum } from '../../../../common/enums/request-type';
import { ClosedRequest } from '../../../../common/models/closed-request.model';
import { CurrentAssignee } from '../../../../common/models/assignee.model';
import {
  ApiResponse,
  Meta,
} from '../../../../common/models/api-response.model';
import { FilteredTableComponent } from '../../../../common/components/filtered-table/filtered-table.component';
import { FilteredTablePayload } from '../../../../common/components/filtered-table/filtered-table.model';

@Component({
  selector: 'lib-on-hold',
  imports: [
    DatePipe,
    TranslocoPipe,
    AvatarComponent,
    ActionListComponent,
    TruncatePipe,
    TrimTooltipDirective,
    RequestCreatorComponent,
    RouterLink,
    TenantPathPipe,
    UserDropdownViewComponent,
    FilteredTableComponent,
    FilterTableColByFeatFlagPipe,
    RelatedRequestsDropdownComponent,
  ],
  templateUrl: './on-hold.component.html',
  styleUrl: './on-hold.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class OnHoldComponent {
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;
  protected readonly COLUMN_IDENTIFIERS = COLUMN_IDENTIFIERS;
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly myOrganizationService = inject(MyOrganizationService);
  private readonly tenantService = inject(TenantService);
  private readonly searchService = inject(SearchService);
  private readonly featureFlagService = inject(FeatureFlagsService);

  protected readonly showRelatedRequestsColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.parent_and_child_approach
    )
  );

  /*********** input *********/
  totalItems = input<number>(0);

  /*********** table *********/
  isLoading = signal<boolean>(false);
  dataSource = signal<ClosedRequest[]>([]);
  meta = signal<Meta | undefined>(undefined);

  private readonly filterState = [StepStatusEnum.ON_HOLD];

  private readonly viewDetailsAction: ActionList = {
    ...ON_HOLD_VIEW_DETAILS_ACTION,
    label: this.transloco.translate(ON_HOLD_VIEW_DETAILS_ACTION.label),
  };

  columns = computed<TableColumns[]>(() =>
    ON_HOLD_DEFAULT_COLUMNS.filter(col =>
      isFeatureFlaggedColumnVisible(col.header, {
        showRelatedRequestsColumn: this.showRelatedRequestsColumn(),
      })
    ).map(column => {
      const mappedColumn: any = {
        ...column,
        header: this.transloco.translate(column.header),
      };
      const routerLinkFn = (column as any).viewIconRouterLink;
      if (routerLinkFn) {
        mappedColumn.viewIconRouterLink = (row: any) => [
          this.tenantService.tenantPath(routerLinkFn(row)[0]),
        ];
      }
      return mappedColumn;
    })
  );

  tableFilterTabs = signal<TableFilterTab[]>(
    ON_HOLD_TABLE_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );

  activeTableFilterTab = computed(
    () => this.payload().tab ?? this.tableFilterTabs()[0]
  );

  filterOptions: FilterOption[] = ON_HOLD_FILTER_OPTIONS.map(option => ({
    ...option,
    label: this.transloco.translate(option.label),
    placeholder: option.placeholder
      ? this.transloco.translate(option.placeholder)
      : '',
  }));

  readonly filterMapping = ON_HOLD_FILTER_MAPPING;

  payload = signal<FilteredTablePayload>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    sortColumn: { key: '', value: 'asc' },
    search: '',
    filters: {},
    tab: this.tableFilterTabs()[0],
  });

  checkReturnedObservable = (): Observable<ApiResponse<ClosedRequest>> => {
    this.isLoading.set(true);
    this.meta.set(undefined);

    const { page, size, sortColumn, search, filters } = this.payload();

    return this.myOrganizationService
      .getClosedRequestsList(
        page,
        size,
        this.filterState,
        sortColumn,
        this.searchService.checkSearchLength(search),
        null,
        null,
        filters
      )
      .pipe(finalize(() => this.isLoading.set(false)));
  };

  handleOnHoldSuccess(res: ApiResponse<ClosedRequest>) {
    this.dataSource.set(
      res.data.map(row => {
        const assignees = row.current_assignees.map(
          (assignee: CurrentAssignee) => assignee.data
        ) as unknown as CurrentAssignee[];
        const assigneeType = row.current_assignees.length
          ? row.current_assignees.map(
              (assignee: CurrentAssignee) => assignee.type
            )
          : [];
        return {
          ...row,
          assignees,
          assigneeType,
          actionList: this.getViewActionList(row.id),
        };
      })
    );
    this.meta.set(res.meta);
  }

  handleApiCall() {
    this.checkReturnedObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this.handleOnHoldSuccess(res),
      });
  }

  protected getRelatedRequestViewLink = (childFlow: { id: number }) =>
    this.tenantService.tenantPath(
      `${EMPLOYEE_ROUTES.MY_ORGANIZATION.REQUEST}/${RequestTypeEnum.ACTION}/${childFlow.id}`
    );

  getTaskRouterLink(taskId: number): string[] {
    return [
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.MY_ORGANIZATION.REQUEST}/${RequestTypeEnum.ACTION}/${taskId.toString()}`
      ),
    ];
  }

  getViewActionList(taskId: number): ActionList[] {
    return [
      {
        ...this.viewDetailsAction,
        routerLinks: [
          this.tenantService.tenantPath(
            `${EMPLOYEE_ROUTES.MY_ORGANIZATION.REQUEST}/${RequestTypeEnum.ACTION}/${taskId.toString()}`
          ),
        ],
      },
    ];
  }
}
